import { useState } from "react";
import { getCookie, writeCookie } from "./cookie-notice";
import { ExternalLink } from "./external-link";
import { IconX } from "./icons";

const COOKIE_PREFIX = "banner-dismissed-";

export interface AnnouncementBannerProps {
  dismissKey: string;
  badge: string;
  icon?: React.ReactNode;
  title: string;
  description: string;
  link?: { href: string; label: string };
  expiresAt?: number;
}

export const AnnouncementBanner = ({
  dismissKey,
  badge,
  icon,
  title,
  description,
  link,
  expiresAt,
}: AnnouncementBannerProps) => {
  const [dismissed, setDismissed] = useState(
    () => getCookie(`${COOKIE_PREFIX}${dismissKey}`) === "1",
  );

  if (dismissed || (!title && !description)) return null;
  if (expiresAt !== undefined && Date.now() > expiresAt) return null;

  const handleDismiss = () => {
    setDismissed(true);
    writeCookie(`${COOKIE_PREFIX}${dismissKey}`, "1", 365);
  };

  return (
    <div className="relative overflow-hidden rounded-lg bg-indigo-100 border border-indigo-200 p-4">
      <div className="absolute inset-0 announcement-banner-bg" />
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="absolute top-2 right-2 z-10 flex items-center justify-center w-7 h-7 text-indigo-300 hover:text-indigo-400"
      >
        <IconX variant="sm" color="#7C91FF" />
      </button>
      <div className="relative pr-8">
        <span className="inline-flex items-center rounded-full px-3 py-1 bg-indigo-200 border border-indigo-300 text-[10px] font-semibold tracking-wide text-indigo-600 uppercase">
          {badge}
        </span>
        <h3 className="mt-2 text-base font-semibold text-black flex items-center gap-1.5">
          {icon}
          {title}
        </h3>
        <p className="mt-1 text-sm text-black-600">
          {description}
          {link ? (
            <>
              {" "}
              <ExternalLink
                href={link.href}
                className="font-medium underline text-indigo-400"
              >
                {link.label} →
              </ExternalLink>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
};
